import { mapState, mapGetters } from 'vuex'
import { mapFilters } from './helpers'
import { cloneDeep, orderBy } from 'lodash'
import { dateRenderer, priceWithCurrencyRenderer, metaValuesRenderer } from '@/lib/generic.utils'
import * as apiTo from '@api/api-routes'
// Components
import AgGridTextDateFilter from '@components/ag-grid-helpers/AgGridTextDateFilter'
import AgGridPriceFilter from '@components/ag-grid-helpers/AgGridPriceFilter'

export const previewReportMixin = {
  data: () => ({
    totalRows: null,
    isGenerateBtnLoading: false,
    isSaveBtnLoading: false
  }),

  computed: {
    ...mapState('reports', [
      'newReport',
      'reportToEdit',
      'visibleColumns',
      'reportTypes',
      'savedReports',
      'routerView'
    ]),

    ...mapGetters('reports', ['isReportUpdated']),

    allColumns() {
      let colCategories
      if (this.routerView === 'newReportPage') colCategories = this.newReport.column_categories
      else colCategories = this.reportToEdit.columns.all_columns

      return colCategories.reduce((acc, curr) => {
        acc.push(...orderBy(curr.columns, 'order_column'))
        return acc
      }, [])
    },

    columnDefs() {
      const columns = this.allColumns

      return columns.reduce((acc, curr) => {
        let colDef = this.getColDefs(curr)
        if (this.dateCols.concat(this.priceCols, this.numberCols).includes(curr.slug)) {
          colDef = { ...colDef, ...mapFilters[curr.slug] }
        }

        acc.push(colDef)
        return acc
      }, [])
    },

    gridOptions() {
      return {
        frameworkComponents: {
          AgGridTextDateFilter,
          AgGridPriceFilter
        },
        defaultColDef: {
          floatingFilter: true,
          filter: 'agTextColumnFilter',
          suppressMenu: true,
          floatingFilterComponentParams: {
            debounceMs: 800
          }
        },
        columnDefs: this.columnDefs,
        cacheBlockSize: 40,
        pagination: false,
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        domLayout: 'normal',
        getRowNodeId: null
      }
    },

    reportName: {
      get() {
        if (this.routerView === 'newReportPage') return this.newReport.title
        return this.reportToEdit.title
      },
      set(newVal) {
        const report = this.routerView === 'newReportPage' ? cloneDeep(this.newReport) : cloneDeep(this.reportToEdit)
        if (newVal && report.title === newVal.trim()) return
        report.title = newVal
        this.routerView === 'newReportPage' ? this.updateNewReport(report) : this.setReportToEdit(report)
      }
    }
  },

  watch: {
    visibleColumns: 'toggleVisibleColumns'
  },

  methods: {
    updateRowsCount(rowsCount) {
      this.totalRows = rowsCount
    },

    getSelectedColIds() {
      if (this.routerView === 'newReportPage') return this.newReport.preselected_columns
      return this.reportToEdit.columns.selected_col_ids
    },

    getColDefs(col) {
      const selectedColIds = this.getSelectedColIds()

      return {
        headerName: col.name,
        field: col.slug,
        cellRenderer: params => {
          if (this.dateCols.includes(col.slug)) {
            return dateRenderer(params.value)
          } else if (this.priceCols.includes(col.slug)) {
            const currency = this.normalizedPriceCols.includes(col.slug) ? 'normalized_currency' : 'currency'
            params.value = params.data[col.slug]
            return priceWithCurrencyRenderer(params, currency)
          } else if (col.is_meta_data_column && params.data.meta_data) {
            const metaDataName = col.slug.split('meta_data.')[1]
            params.value = params.data.meta_data[metaDataName]
            return metaValuesRenderer(params)
          } else if (this.noteCols.includes(col.slug)) {
            if (!params.data[col.slug] || !params.data[col.slug].length) return 'N/A'

            return params.data[col.slug].map(item => item.body).join(';')
          } else if (this.linkCols.includes(col.slug)) {
            if (!params.data[col.slug] || !params.data[col.slug].length) return 'N/A'

            return params.data[col.slug].map(item => item.url).join(';')
          } else if (this.licenseCols.includes(col.slug)) {
            if (!params.data[col.slug] || !params.data[col.slug].length) return 'N/A'

            return params.data[col.slug].map(item => item.name).join(';')
          } else if (this.qualifiersCols.includes(col.slug)) {
            if (!params.data[col.slug] || !params.data[col.slug].length) return 'N/A'

            return params.data[col.slug].map(item => item.name).join(';')
          }

          return params.value || 'N/A'
        },
        minWidth: 140,
        hide: !selectedColIds.includes(col.id)
      }
    },

    selectedColumns() {
      const selectedColIds = this.visibleColumns || this.getSelectedColIds()
      const columns = this.allColumns

      return columns.reduce((acc, curr) => {
        if (selectedColIds.includes(curr.id)) acc[curr.slug] = curr.name
        return acc
      }, {})
    },

    toggleVisibleColumns(visibleColumns) {
      const columns = this.allColumns
      const showHideColumns = columns.reduce(
        (acc, curr) => {
          if (visibleColumns.includes(curr.id)) acc.show.push(curr.slug)
          else acc.hide.push(curr.slug)
          return acc
        },
        { show: [], hide: [] }
      )

      this.$refs.agGrid.toggleVisibleColumns(showHideColumns.show)
      this.$refs.agGrid.toggleVisibleColumns(showHideColumns.hide, false)
      this.$refs.agGrid.purgeServerSideCache()
      if (this.routerView === 'editReportPage') {
        const report = cloneDeep(this.reportToEdit)
        report.columns.selected_col_ids = visibleColumns
        this.setReportToEdit(report)
      }
    },

    getPayload() {
      const gridOptions = this.$refs.agGrid.getGridOptions()
      const filterModel = gridOptions.api.getFilterModel()
      const sortModel = gridOptions.columnApi
        .getColumnState()
        .filter(col => !!col.sort)
        .map(col => ({ colId: col.colId, sort: col.sort }))
      const selectedColumns = gridOptions.columnApi.getAllDisplayedColumns().reduce((acc, curr) => {
        acc[curr.colId] = curr.colDef.headerName
        return acc
      }, {})

      return {
        filterModel,
        sortModel,
        selectedColumns
      }
    },

    getSaveReportPayload() {
      const { filters, sorts, columns, selectedColumns } = this.savePayloadAlterColumns()

      columns.forEach(category => {
        let visibleColumns = category.columns.filter(col => col.slug in selectedColumns)
        const hiddenColumns = category.columns.filter(col => !(col.slug in selectedColumns))
        visibleColumns = this.visibleColumns.map(colId => visibleColumns.find(col => col.id === colId))

        category.columns = [...visibleColumns, ...hiddenColumns]
      })

      return {
        filters,
        sorts,
        columns: {
          all_columns: columns,
          selected_col_ids: this.visibleColumns
        }
      }
    },

    savePayloadAlterColumns() {
      const report = this.routerView === 'newReportPage' ? cloneDeep(this.newReport) : cloneDeep(this.reportToEdit)
      const { filterModel: filters, sortModel: sorts, selectedColumns } = this.getPayload()
      const columnCategories =
        this.routerView === 'newReportPage' ? report.column_categories : report.columns.all_columns

      const columns = columnCategories.map(category => {
        category.columns = category.columns.map(col => {
          if (!(col.slug in selectedColumns)) col.preselected = false
          return col
        })
        return category
      })

      return {
        filters,
        sorts,
        columns,
        selectedColumns
      }
    },

    columnDragStopped(gridOptions) {
      const colNames = gridOptions.columnApi.getAllDisplayedColumns().map(col => col.colId)
      const visibleCols = this.allColumns.filter(col => colNames.includes(col.slug))
      const orderedColIds = colNames.map(colName => visibleCols.find(col => col.slug === colName).id)

      if (this.routerView === 'editReportPage') {
        const report = cloneDeep(this.reportToEdit)
        report.columns.selected_col_ids = orderedColIds
        this.setReportToEdit(report)
      }
      this.setVisibleColumns(orderedColIds)
    },

    generate() {
      const payload = this.getPayload()
      const reportId = this.routerView === 'newReportPage' ? this.newReport.id : this.reportToEdit.report_type.id

      this.isSaveBtnLoading = true
      apiTo
        .generateReport(reportId, payload)
        .then(response => {
          this.$store.dispatch(
            'createNotificationToast',
            {
              message: response.data.messages.default,
              color: response.data.status
            },
            { root: true }
          )
        })
        .finally(() => (this.isSaveBtnLoading = false))
    }
  },
  created() {
    this.dateCols = ['start_date', 'end_date', 'cancellation_deadline']
    this.priceCols = [
      'price',
      'normalized_price',
      'list_price',
      'first_offer_price',
      'forecasted_price',
      'forecasted_local_price',
      'cost_per_use_original',
      'cost_per_use_local'
    ]
    this.numberCols = ['usage', 'publications']
    this.normalizedPriceCols = ['normalized_price', 'cost_per_use_local']
    this.noteCols = ['notes', 'license_notes']
    this.linkCols = ['links', 'license_links']
    this.licenseCols = ['licenses']
    this.qualifiersCols = ['license_qualifiers']
  }
}
